import { CustomInput, Upload, CustomTooltip, Loader } from 'components';
import { DeleteIcon } from 'assets/icons';
import { useForm, Controller } from 'react-hook-form';
import * as S from '../newApp/NewAppTab.styled';
import Footer from '../../footer/Footer';
import ChooseCategory from '../../chooseCategory/ChooseCategory';
import { formDataFromObject } from 'utils';
import PropTypes from 'prop-types';
import { manageAppsAPI } from 'services/manageApps.service';
import { useState, useRef, useCallback, useMemo } from 'react';

const URL_REGEX = /^https:\/\/[\w.-]+\.[a-z]{2,}(\/\S*)?$/i;
const DEBOUNCE_MS = 800;

const getDomain = (url) => {
  let domain = url?.toLowerCase().trim() || '';
  domain = domain.replace(/^https?:\/\//, '');
  domain = domain.split('/')[0];
  domain = domain.replace(/^www\./, '');
  return domain;
};

const base64ToFile = (base64String, fileName) => {
  const [header, data] = base64String.split(',');
  const mime = header.match(/:(.*?);/)[1];
  const bytes = atob(data);
  const arr = new Uint8Array(bytes.length);
  for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
  return new File([arr], fileName, { type: mime });
};

export const WebsiteAppTab = ({ handleClose, chooseAction }) => {
  const { data: categories } = manageAppsAPI.useGetAllAppsCategoriesQuery();
  const { data: appList } = manageAppsAPI.useGetAppsListQuery('');
  const [saveWebsiteApp, result] = manageAppsAPI.useSaveWebsiteAppMutation();

  const existingWebsiteDomains = useMemo(() => {
    if (!appList) return [];
    return appList
      .filter((app) => !!app.app_type_assoc)
      .map((app) => getDomain(app.pba_url))
      .filter(Boolean);
  }, [appList]);
  const [urlValidating, setUrlValidating] = useState(false);
  const [urlServerError, setUrlServerError] = useState('');
  const [faviconPreview, setFaviconPreview] = useState(null);
  const userUploadedIcon = useRef(false);
  const debounceRef = useRef(null);
  const [triggerValidate] = manageAppsAPI.useLazyValidateWebsiteUrlQuery();

  const {
    handleSubmit,
    setValue,
    setError,
    clearErrors,
    control,
    formState: { errors, isValid, isSubmitting },
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      websiteName: '',
      websiteUrl: '',
      appIcon: null,
      category: '',
    },
  });

  const validateUrlOnServer = useCallback(
    (url) => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      setUrlServerError('');

      // Clear previous auto-fetched favicon when URL changes
      if (!userUploadedIcon.current) {
        setFaviconPreview(null);
        setValue('appIcon', null, { shouldValidate: false });
      }

      if (!url || !URL_REGEX.test(url)) {
        setUrlValidating(false);
        return;
      }

      if (existingWebsiteDomains.includes(getDomain(url))) {
        setUrlServerError('A website with this domain already exists');
        setUrlValidating(false);
        return;
      }

      setUrlValidating(true);
      debounceRef.current = setTimeout(async () => {
        try {
          const res = await triggerValidate(url, true).unwrap();
          if (!res.exist) {
            setUrlServerError('Website URL is not reachable');
          } else {
            setUrlServerError('');
            // Auto-set favicon if user hasn't manually uploaded an icon
            if (!userUploadedIcon.current && res.favicon) {
              const file = base64ToFile(res.favicon, 'favicon.png');
              setFaviconPreview(res.favicon);
              setValue('appIcon', file, { shouldValidate: true });
            }
          }
        } catch {
          setUrlServerError('Failed to validate URL');
        } finally {
          setUrlValidating(false);
        }
      }, DEBOUNCE_MS);
    },
    [triggerValidate, setValue, existingWebsiteDomains],
  );

  const onSubmit = async (data) => {
    if (urlServerError || urlValidating) return;

    const catValue = categories.find((cat) => cat.application_type_name === data.category);
    const newData = {
      websiteName: data.websiteName.trim(),
      websiteUrl: data.websiteUrl.trim(),
      appIcon: data.appIcon,
      category: catValue.application_type_id,
    };

    const res = await saveWebsiteApp(formDataFromObject(newData));
    chooseAction(res);
    const appId = res.data?.apps?.appId || '';
    handleClose(appId);
  };

  return (
    <S.Form onSubmit={handleSubmit(onSubmit)} id='websiteApp'>
      {result.isLoading && <Loader.Global delay={0} />}
      <S.Container>
        <S.Field>
          <S.Label htmlFor='websiteUrl'>Website URL:</S.Label>
          <Controller
            name='websiteUrl'
            control={control}
            rules={{
              required: 'This field is required',
              pattern: {
                value: URL_REGEX,
                message: 'Please enter a valid HTTPS URL (e.g. https://example.com)',
              },
            }}
            render={({ field: { onChange, value } }) => (
              <CustomInput
                value={value}
                onChange={(e) => {
                  const val = typeof e === 'string' ? e : e?.target?.value ?? '';
                  const trimmedVal = val.replace(/^\s+/, '');
                  onChange(trimmedVal);
                  validateUrlOnServer(trimmedVal);
                }}
                id='websiteUrl'
                name='websiteUrl'
                placeholder='e.g. https://example.com'
                rightIconOnClick={() => {
                  setValue('websiteUrl', '', { shouldDirty: true });
                  clearErrors('websiteUrl');
                  setUrlServerError('');
                }}
                rightIcon={value.length ? <DeleteIcon /> : null}
                containerStyle={{ marginBottom: '26px' }}
                error={!!errors?.websiteUrl || !!urlServerError}
                errorText={errors?.websiteUrl?.message || urlServerError}
              />
            )}
          />
        </S.Field>
        <S.Field>
          <S.Label htmlFor='websiteName'>Website Name:</S.Label>
          <Controller
            name='websiteName'
            control={control}
            rules={{
              required: 'This field is required',
              validate: (value) => value?.trim().length > 0 || 'Input cannot be only spaces',
            }}
            render={({ field: { onChange, value } }) => (
              <CustomInput
                value={value}
                onChange={onChange}
                id='websiteName'
                name='websiteName'
                rightIconOnClick={() => {
                  setValue('websiteName', '', { shouldDirty: true });
                  clearErrors('websiteName');
                }}
                rightIcon={value.length ? <DeleteIcon /> : null}
                containerStyle={{ marginBottom: '26px' }}
                error={!!errors?.websiteName}
                errorText={errors?.websiteName?.message}
              />
            )}
          />
        </S.Field>
        <S.Field>
          <S.ReqWrapper>
            <S.Label htmlFor='appIcon'>Website Icon:</S.Label>
            <S.Requirements>
              Requirements
              <CustomTooltip
                content={<S.Marker>?</S.Marker>}
                text={
                  <p>
                    JPEG or PNG.
                    <br />
                    Please upload image size 512*512.
                  </p>
                }
              />
            </S.Requirements>
          </S.ReqWrapper>
          <Controller
            name='appIcon'
            control={control}
            rules={{
              required: 'This field is required',
            }}
            render={({ field: { onChange } }) => (
              <Upload
                accept='image/jpeg, image/jpg, image/png, image/gif'
                buttonText='Choose File'
                id='appIcon'
                size={Upload.Size?.Big}
                maxWidth={512}
                maxHeight={512}
                onChange={(file) => {
                  userUploadedIcon.current = !!file;
                  setFaviconPreview(null);
                  clearErrors('appIcon');
                  onChange(file);
                }}
                onError={(msg) => setError('appIcon', { type: 'validate', message: msg })}
                value={faviconPreview}
                environment='accountSettings'
                containerStyle={{ marginBottom: '26px' }}
                error={!!errors?.appIcon}
                errorText={errors?.appIcon?.message}
              />
            )}
          />
        </S.Field>
        <S.Field>
          <S.Label htmlFor='category'>Category:</S.Label>
          <Controller
            name='category'
            control={control}
            rules={{
              required: 'This field is required',
            }}
            render={({ field: { onChange, value } }) => (
              <ChooseCategory
                id='category'
                category={value}
                onChange={onChange}
                error={!!errors?.category}
                errorText={errors?.category?.message}
                setValue={setValue}
              />
            )}
          />
        </S.Field>
      </S.Container>
      <Footer
        handleClose={handleClose}
        handleSaveApp={handleSubmit}
        typeSaveBtn='submit'
        isDisabled={
          !isValid || !!urlServerError || urlValidating || isSubmitting || result.isLoading
        }
      />
    </S.Form>
  );
};

WebsiteAppTab.propTypes = {
  handleClose: PropTypes.func.isRequired,
  chooseAction: PropTypes.func,
};
